/**
 * Reward Content Display Component
 * Renders reward content based on its type
 */

import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  Chip,
} from '@mui/material'
import {
  ContentCopy,
  OpenInNew,
  LockOpen,
  QrCode2,
  Image as ImageIcon,
  Close,
  Fullscreen,
} from '@mui/icons-material'
import { ThemedQRCode } from '@/components/ThemedQRCode'
import { GiftRewardContentType } from '@api/types/rewards'
import type { GiftRewardInstanceDto, UserRewardDto } from '@api/types/rewards'

interface RewardContentDisplayProps {
  reward: GiftRewardInstanceDto | UserRewardDto
  showQRAction?: () => void
}

export function RewardContentDisplay({ reward, showQRAction }: RewardContentDisplayProps) {
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)

  const content = reward.content as Record<string, unknown> | null

  // GiftRewardInstanceDto exposes title/contentType nested under .template;
  // UserRewardDto exposes them at the top level.
  const title = 'template' in reward ? reward.template?.title : reward.title
  const contentType = 'template' in reward ? reward.template?.contentType : reward.contentType

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  switch (contentType) {
    case GiftRewardContentType.Image: {
      const imageUrl = content?.imageUrl as string | undefined
      if (!imageUrl) {
        return (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <ImageIcon sx={{ fontSize: 64, mb: 1 }} />
            <Typography>No image available</Typography>
          </Box>
        )
      }
      return (
        <>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover .image-overlay': {
                opacity: 1,
              },
            }}
            onClick={() => setImageDialogOpen(true)}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={title || 'Reward image'}
              sx={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'contain',
                display: 'block',
              }}
            />
            <Box
              className="image-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            >
              <Typography color="white">Click to enlarge</Typography>
            </Box>
          </Box>

          {/* Image lightbox dialog */}
          <Dialog
            open={imageDialogOpen}
            onClose={() => setImageDialogOpen(false)}
            maxWidth="lg"
          >
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={() => setImageDialogOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <Close />
              </IconButton>
              <Box
                component="img"
                src={imageUrl}
                alt={title || 'Reward image'}
                sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
              />
            </Box>
          </Dialog>
        </>
      )
    }

    case GiftRewardContentType.URL: {
      const linkUrl = content?.linkUrl as string | undefined
      const linkLabel = (content?.linkLabel as string) || linkUrl || 'Visit Link'
      if (!linkUrl) {
        return (
          <Typography color="text.secondary">No link available</Typography>
        )
      }
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {linkLabel}
            </Typography>
            <Tooltip title={copiedUrl ? 'Copied!' : 'Copy URL'}>
              <IconButton size="small" onClick={() => handleCopyUrl(linkUrl)}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNew />}
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Link
            </Button>
          </Box>
        </Paper>
      )
    }

    case GiftRewardContentType.Text: {
      const textContent = content?.textContent as string | undefined
      if (!textContent) {
        return (
          <Typography color="text.secondary">No content available</Typography>
        )
      }
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography
            sx={{ whiteSpace: 'pre-wrap' }}
            dangerouslySetInnerHTML={{ __html: textContent }}
          />
        </Paper>
      )
    }

    case GiftRewardContentType.QR_Redeemable: {
      const displayKey = reward.displayKey || ''
      const qrData = `R:${displayKey}`

      return (
        <Box sx={{ textAlign: 'center' }}>
          {/* QR Code display */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <ThemedQRCode data={qrData} size={220} />
          </Box>

          {/* Instructions */}
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Present this QR code to redeem your reward
          </Typography>

          {/* Fullscreen button */}
          {showQRAction && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Fullscreen />}
              onClick={showQRAction}
            >
              Fullscreen
            </Button>
          )}
        </Box>
      )
    }

    case GiftRewardContentType.Adventure_Unlock: {
      const adventureId = content?.adventureId as string | undefined
      const adventureTitle = (content?.adventureTitle as string) || 'Adventure'
      return (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              bgcolor: 'success.light',
              color: 'success.contrastText',
            }}
          >
            <LockOpen fontSize="large" />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Chip label="Adventure Unlocked" color="success" size="small" sx={{ mb: 1 }} />
            <Typography variant="h6">{adventureTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              You have unlocked access to this adventure
            </Typography>
          </Box>
          {adventureId && (
            <Button
              variant="outlined"
              component={RouterLink}
              to={`/adventures/${adventureId}`}
            >
              View Adventure
            </Button>
          )}
        </Paper>
      )
    }

    default:
      return (
        <Typography color="text.secondary">
          Unknown content type
        </Typography>
      )
  }
}
